import React from "react";
import { createPortal } from "react-dom";
import type { PropsWithChildren, ReactNode } from "react";
import type { SourceRef } from "./types";

interface HiddenSourceFieldsProps {
  prefix: string;
  source?: SourceRef;
  nestUnderSource?: boolean;
}

export function HiddenSourceFields({ prefix, source = {}, nestUnderSource = true }: HiddenSourceFieldsProps) {
  const fieldPrefix = nestUnderSource ? `${prefix}[source]` : prefix;

  return (
    <>
      <input type="hidden" name={`${fieldPrefix}[type]`} value={source.type || ""} />
      <input type="hidden" name={`${fieldPrefix}[label]`} value={source.label || ""} />
      <input type="hidden" name={`${fieldPrefix}[id]`} value={source.id?.toString() || ""} />
      <input type="hidden" name={`${fieldPrefix}[document_id]`} value={source.document_id?.toString() || ""} />
      <input type="hidden" name={`${fieldPrefix}[source_page]`} value={source.source_page?.toString() || ""} />
    </>
  );
}

interface ModalProps extends PropsWithChildren {
  title: string;
  onClose: () => void;
  size?: "default" | "wide";
}

export function Modal({ title, onClose, children, size = "default" }: ModalProps) {
  const widthClass = size === "wide" ? "max-w-[96vw] xl:max-w-[92rem]" : "max-w-4xl";

  const modalContent = (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-neutral-950/45 px-4 py-6 print:hidden" role="dialog" aria-modal="true">
      <div className={`w-full ${widthClass} overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-xl dark:border-neutral-800 dark:bg-neutral-900`}>
        <div className="flex items-start justify-between gap-3 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-600 dark:text-emerald-400">Edit section</p>
            <h3 className="mt-0.5 text-base font-semibold text-neutral-950 dark:text-white">{title}</h3>
          </div>
          <button type="button" className="app-button-secondary" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="max-h-[80vh] overflow-y-auto px-4 py-4">{children}</div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return modalContent;

  return createPortal(modalContent, document.body);
}

interface SectionHeaderProps {
  kicker: string;
  title: string;
  onEdit: () => void;
  editable: boolean;
  actions?: ReactNode;
}

export function SectionHeader({ kicker, title, onEdit, editable, actions }: SectionHeaderProps) {
  return (
    <div className="flex items-start justify-between gap-2.5">
      <div>
        <p className="app-kicker">{kicker}</p>
        <h3 className="mt-0.5 text-sm font-semibold text-neutral-950 dark:text-white">{title}</h3>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2 print:hidden">
        {actions}
        {editable ? (
          <button type="button" className="app-button-secondary" onClick={onEdit}>
            Edit section
          </button>
        ) : null}
      </div>
    </div>
  );
}

interface SummaryCardProps {
  label: string;
  value: string | number;
  body: ReactNode;
}

export function SummaryCard({ label, value, body }: SummaryCardProps) {
  return (
    <article className="rounded-lg border border-neutral-200 bg-neutral-50/80 p-3 dark:border-neutral-800 dark:bg-neutral-950/60">
      <p className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-400 dark:text-neutral-500">{label}</p>
      <p className="mt-1.5 text-2xl font-semibold text-neutral-950 dark:text-white">{value}</p>
      <p className="mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">{body}</p>
    </article>
  );
}
